import * as z from 'zod';
import {normalizeZipCode} from '../zipCode/schema';

export const ADDRESS_REQUIRED_VALIDATION_MSG = '必須項目です';
export const ADDRESS_ZIP_CODE_VALIDATION_MSG = '半角数字7桁で入力してください';
export const ADDRESS_TEXT_MAX_LENGTH = 80;
export const ADDRESS_TEXT_MAX_LENGTH_VALIDATION_MSG = `${ADDRESS_TEXT_MAX_LENGTH}文字以内で入力してください`;
export const ADDRESS_UNUSABLE_CHAR_VALIDATION_MSG =
  '使用できない文字が含まれています';

// もう一つの住所の見出し。在住地に関わらず統一する
export const ALTERNATE_ADDRESS_LABEL = '帰省（休暇）先の住所';

export const getAlternateAddressLabel = (): string => ALTERNATE_ADDRESS_LABEL;

// 「現在の住所と同じ」チェックの文言。海外は現住所が海外のため、帰省(休暇)先も
// 海外＝日本国内に住所がない、の意味になるよう在住地で出し分ける
export const ALTERNATE_ADDRESS_SAME_CHECKBOX_LABEL_DOMESTIC =
  '現在の住所と同じ';
export const ALTERNATE_ADDRESS_SAME_CHECKBOX_LABEL_OVERSEAS =
  '帰省（休暇）先の住所も海外（日本国内に住所がない）';

export const getAlternateAddressSameCheckboxLabel = (
  isOverseas: boolean,
): string =>
  isOverseas
    ? ALTERNATE_ADDRESS_SAME_CHECKBOX_LABEL_OVERSEAS
    : ALTERNATE_ADDRESS_SAME_CHECKBOX_LABEL_DOMESTIC;

// もう一つの住所が未入力のときの表示。閲覧・確認で項目を常に出し状態を明示する。
export const ALTERNATE_ADDRESS_SAME_AS_CURRENT_TEXT = '現住所と同じ';
export const ALTERNATE_ADDRESS_OVERSEAS_TEXT = '海外';

// 表示用のもう一つの住所テキスト。入力があればその住所、無ければ
// 海外在住は「海外」(帰省先も海外＝日本国内に住所なし)、国内在住は「現住所と同じ」を返す
export const getAlternateAddressDisplayText = (
  alternateAddressText: string,
  isOverseas: boolean,
): string => {
  if (alternateAddressText !== '') {
    return alternateAddressText;
  }

  return isOverseas
    ? ALTERNATE_ADDRESS_OVERSEAS_TEXT
    : ALTERNATE_ADDRESS_SAME_AS_CURRENT_TEXT;
};

// 現住所が国内なら「現在住所と同じ」を初期ON、海外なら初期OFFにする
export const isSameAsCurrentAddressDefault = (isOverseas: boolean): boolean =>
  !isOverseas;

// もう一つの住所にいずれかの入力があるか。居住地切替でチェック状態を
// 初期値へ上書きしてよいか判定するために使う
export const hasAlternateAddressInput = (values: AddressFormValues): boolean =>
  values.alternatePrefectureID !== '' ||
  values.alternateZipCode !== '' ||
  values.alternateAddress1 !== '' ||
  values.alternateAddress2 !== '' ||
  values.alternateBuilding !== '';

// 郵便番号はハイフン有無どちらの入力も受け付け、送信時にハイフンを除去する
const zipCodeRegex = /^\d{3}-?\d{4}$/;

// 全角/半角などの表記ゆれはAPI側で正規化されるが、絵文字と制御文字だけは
// 正規化できず400で弾かれる。同じ判定をここに持ち、項目ごとにエラーを出す
const isUnusableCodePoint = (codePoint: number) =>
  codePoint < 0x20 ||
  codePoint === 0x7f ||
  (codePoint >= 0x80 && codePoint <= 0x9f) ||
  (codePoint >= 0x1f000 && codePoint <= 0x1faff) ||
  (codePoint >= 0x2600 && codePoint <= 0x27bf) ||
  (codePoint >= 0x2b00 && codePoint <= 0x2bff) ||
  codePoint === 0x200d ||
  (codePoint >= 0xfe00 && codePoint <= 0xfe0f) ||
  codePoint === 0x20e3;

// サロゲートペアを1文字として扱うためコードポイント単位で走査する
export const containsUnusableAddressChar = (value: string) =>
  Array.from(value).some((char) =>
    isUnusableCodePoint(char.codePointAt(0) ?? 0),
  );

// 住所フォームのフィールド。都道府県は Mantine の Select に合わせて文字列で保持する。
// alternate は現住所とは別の「もう一つの住所」。常に日本の住所で電話番号は持たない
export const addressFormShape = {
  isOverseas: z.boolean(),
  prefectureID: z.string(),
  zipCode: z.string(),
  address1: z.string(),
  address2: z.string(),
  building: z.string(),
  // trueなら現在住所と同じ(alternateは送らない)。falseならalternateを入力する
  isSameAsCurrentAddress: z.boolean(),
  alternatePrefectureID: z.string(),
  alternateZipCode: z.string(),
  alternateAddress1: z.string(),
  alternateAddress2: z.string(),
  alternateBuilding: z.string(),
};

export type AddressFormValues = {
  isOverseas: boolean;
  prefectureID: string;
  zipCode: string;
  address1: string;
  address2: string;
  building: string;
  isSameAsCurrentAddress: boolean;
  alternatePrefectureID: string;
  alternateZipCode: string;
  alternateAddress1: string;
  alternateAddress2: string;
  alternateBuilding: string;
};

export const ADDRESS_INITIAL_FORM_VALUES: AddressFormValues = {
  isOverseas: false,
  prefectureID: '',
  zipCode: '',
  address1: '',
  address2: '',
  building: '',
  // 初期の現住所は国内なので「現在住所と同じ」を初期ONにする
  isSameAsCurrentAddress: true,
  alternatePrefectureID: '',
  alternateZipCode: '',
  alternateAddress1: '',
  alternateAddress2: '',
  alternateBuilding: '',
};

// 現住所と alternate で同じ検証を使い回すため、対象フィールドのキーを差し替える
type AddressFieldKeys = {
  prefectureID: string;
  zipCode: string;
  address1: string;
  address2: string;
  building: string;
};

const CURRENT_ADDRESS_FIELD_KEYS: AddressFieldKeys = {
  prefectureID: 'prefectureID',
  zipCode: 'zipCode',
  address1: 'address1',
  address2: 'address2',
  building: 'building',
};

const ALTERNATE_ADDRESS_FIELD_KEYS: AddressFieldKeys = {
  prefectureID: 'alternatePrefectureID',
  zipCode: 'alternateZipCode',
  address1: 'alternateAddress1',
  address2: 'alternateAddress2',
  building: 'alternateBuilding',
};

const addressFieldValue = (values: AddressFormValues, key: string): string =>
  (values as unknown as Record<string, string>)[key] ?? '';

const refineAddressFields = <Output extends AddressFormValues, Input>(
  schema: z.ZodType<Output, z.ZodTypeDef, Input>,
  keys: AddressFieldKeys,
  skip: (values: AddressFormValues) => boolean,
  requiredMessage: string,
) =>
  schema
    .refine(
      (values: AddressFormValues) =>
        skip(values) || addressFieldValue(values, keys.prefectureID) !== '',
      {
        message: requiredMessage,
        path: [keys.prefectureID],
      },
    )
    .refine(
      (values: AddressFormValues) =>
        skip(values) || addressFieldValue(values, keys.zipCode) !== '',
      {
        message: requiredMessage,
        path: [keys.zipCode],
      },
    )
    .refine(
      (values: AddressFormValues) => {
        const zipCode = addressFieldValue(values, keys.zipCode);
        return skip(values) || zipCode === '' || zipCodeRegex.test(zipCode);
      },
      {
        message: ADDRESS_ZIP_CODE_VALIDATION_MSG,
        path: [keys.zipCode],
      },
    )
    .refine(
      (values: AddressFormValues) =>
        skip(values) || addressFieldValue(values, keys.address1).trim() !== '',
      {
        message: requiredMessage,
        path: [keys.address1],
      },
    )
    .refine(
      (values: AddressFormValues) =>
        skip(values) || addressFieldValue(values, keys.address2).trim() !== '',
      {
        message: requiredMessage,
        path: [keys.address2],
      },
    )
    // 文字数も検証をスキップする条件下ならスキップする。入力欄が非表示になるため、
    // 常に検証すると画面に出ないエラーで送信できなくなる
    .refine(
      (values: AddressFormValues) =>
        skip(values) ||
        addressFieldValue(values, keys.address1).length <=
          ADDRESS_TEXT_MAX_LENGTH,
      {
        message: ADDRESS_TEXT_MAX_LENGTH_VALIDATION_MSG,
        path: [keys.address1],
      },
    )
    .refine(
      (values: AddressFormValues) =>
        skip(values) ||
        addressFieldValue(values, keys.address2).length <=
          ADDRESS_TEXT_MAX_LENGTH,
      {
        message: ADDRESS_TEXT_MAX_LENGTH_VALIDATION_MSG,
        path: [keys.address2],
      },
    )
    .refine(
      (values: AddressFormValues) =>
        skip(values) ||
        addressFieldValue(values, keys.building).length <=
          ADDRESS_TEXT_MAX_LENGTH,
      {
        message: ADDRESS_TEXT_MAX_LENGTH_VALIDATION_MSG,
        path: [keys.building],
      },
    )
    .refine(
      (values: AddressFormValues) =>
        skip(values) ||
        !containsUnusableAddressChar(addressFieldValue(values, keys.address1)),
      {
        message: ADDRESS_UNUSABLE_CHAR_VALIDATION_MSG,
        path: [keys.address1],
      },
    )
    .refine(
      (values: AddressFormValues) =>
        skip(values) ||
        !containsUnusableAddressChar(addressFieldValue(values, keys.address2)),
      {
        message: ADDRESS_UNUSABLE_CHAR_VALIDATION_MSG,
        path: [keys.address2],
      },
    )
    .refine(
      (values: AddressFormValues) =>
        skip(values) ||
        !containsUnusableAddressChar(addressFieldValue(values, keys.building)),
      {
        message: ADDRESS_UNUSABLE_CHAR_VALIDATION_MSG,
        path: [keys.building],
      },
    );

// 現住所は海外在住なら検証しない。もう一つの住所は「現在住所と同じ」なら検証しない。
// いずれも建物名以外を必須にする
export const refineAddress = <Output extends AddressFormValues, Input>(
  schema: z.ZodType<Output, z.ZodTypeDef, Input>,
) => {
  const withCurrentAddress = refineAddressFields(
    schema,
    CURRENT_ADDRESS_FIELD_KEYS,
    (values) => values.isOverseas,
    ADDRESS_REQUIRED_VALIDATION_MSG,
  );

  return refineAddressFields(
    withCurrentAddress,
    ALTERNATE_ADDRESS_FIELD_KEYS,
    (values) => values.isSameAsCurrentAddress,
    ADDRESS_REQUIRED_VALIDATION_MSG,
  );
};

// もう一つの住所。登録が無ければ null が返る
export const AlternateAddressResponse = z.object({
  prefectureID: z.number().nullable(),
  zipCode: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  building: z.string().nullable(),
});

export const AddressResponse = z.object({
  isRegistered: z.boolean(),
  isOverseas: z.boolean(),
  prefectureID: z.number().nullable(),
  zipCode: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  building: z.string().nullable(),
  alternateAddress: AlternateAddressResponse.nullish(),
  modified: z.string().optional(),
});

export const PutAddressResponse = z.object({
  isOverseas: z.boolean(),
  prefectureID: z.number().nullable(),
  zipCode: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  building: z.string().nullable(),
  alternateAddress: AlternateAddressResponse.nullish(),
});

// 住所APIのレスポンスをフォームの入力値へ変換する
export const toAddressFormValues = (
  response: z.infer<typeof AddressResponse>,
): AddressFormValues => {
  const alternate = response.alternateAddress ?? null;

  return {
    isOverseas: response.isOverseas,
    prefectureID: response.prefectureID?.toString() ?? '',
    zipCode: response.zipCode ?? '',
    address1: response.address1 ?? '',
    address2: response.address2 ?? '',
    building: response.building ?? '',
    // alternateがあればチェックOFF(入力欄表示)、無ければ在住地デフォルト(国内ON/海外OFF)
    isSameAsCurrentAddress: alternate
      ? false
      : isSameAsCurrentAddressDefault(response.isOverseas),
    alternatePrefectureID: alternate?.prefectureID?.toString() ?? '',
    alternateZipCode: alternate?.zipCode ?? '',
    alternateAddress1: alternate?.address1 ?? '',
    alternateAddress2: alternate?.address2 ?? '',
    alternateBuilding: alternate?.building ?? '',
  };
};

// 現住所の入力値をリクエストボディへ変換する。海外在住なら住所項目を送らない
export const toCurrentAddressRequestBody = (values: AddressFormValues) => {
  if (values.isOverseas) {
    return {
      isOverseas: true,
      prefectureID: null,
      zipCode: '',
      address1: '',
      address2: '',
      building: '',
    };
  }

  return {
    isOverseas: false,
    prefectureID: Number(values.prefectureID),
    zipCode: normalizeZipCode(values.zipCode),
    // 貼り付けで前後に空白が混入しやすいため落とす。
    // 建物名の中のスペースは部屋番号の区切りとして使われるので残す
    address1: values.address1.trim(),
    address2: values.address2.trim(),
    building: values.building.trim(),
  };
};

// もう一つの住所をネスト形式(住所API)へ変換する。
// 「現在住所と同じ」ならnullを送り、サーバ側で行を削除させる
const toAlternateAddressRequestBody = (values: AddressFormValues) => {
  if (values.isSameAsCurrentAddress) {
    return null;
  }

  return {
    prefectureID: Number(values.alternatePrefectureID),
    zipCode: normalizeZipCode(values.alternateZipCode),
    address1: values.alternateAddress1.trim(),
    address2: values.alternateAddress2.trim(),
    building: values.alternateBuilding.trim(),
  };
};

// 住所APIのリクエストボディへ変換する(現住所 + ネスト形式のalternate)
export const toAddressRequestBody = (values: AddressFormValues) => ({
  ...toCurrentAddressRequestBody(values),
  alternateAddress: toAlternateAddressRequestBody(values),
});

// 登録フロー(profile-temp)向けのフラット形式のリクエストボディへ変換する。
// 「現在住所と同じ」ならalternateを空で送る
export const toAlternateAddressFlatRequestBody = (
  values: AddressFormValues,
) => {
  if (values.isSameAsCurrentAddress) {
    return {
      alternatePrefectureID: null,
      alternateZipCode: '',
      alternateAddress1: '',
      alternateAddress2: '',
      alternateBuilding: '',
    };
  }

  return {
    alternatePrefectureID: Number(values.alternatePrefectureID),
    alternateZipCode: normalizeZipCode(values.alternateZipCode),
    alternateAddress1: values.alternateAddress1.trim(),
    alternateAddress2: values.alternateAddress2.trim(),
    alternateBuilding: values.alternateBuilding.trim(),
  };
};
